import { useMutation } from '@tanstack/react-query';
import { Loader2, Play, ScrollText } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { runCypher, type CypherResult } from '../api/symbols.js';
import { useMcp } from '../api/use-mcp.js';
import { PageHeader } from '../components/page-header.js';

const SAMPLE = 'MATCH (n) RETURN labels(n) as label, count(*) as n LIMIT 25';

export function CypherConsole(): JSX.Element {
  const { name } = useParams<{ name: string }>();
  const { handle, ready } = useMcp();
  const [cypher, setCypher] = useState(SAMPLE);

  const run = useMutation<CypherResult, Error, void>({
    mutationFn: async () => runCypher(handle!, { repo: name ?? '', cypher }),
  });

  if (!name) return <p className="p-8 text-text-muted">Missing repo.</p>;

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-6 py-8">
      <PageHeader
        eyebrow={name}
        title="Cypher console"
        subtitle="Read-only Cypher. Write statements are rejected at the MCP allow-list."
      />

      <div className="overflow-hidden rounded-2xl border border-border-subtle bg-surface/60 backdrop-blur">
        <div className="flex items-center gap-2 border-b border-dashed border-border-subtle px-4 py-2">
          <ScrollText className="h-4 w-4 text-text-muted" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-text-muted">
            query.cypher
          </span>
          <span className="flex-1" />
          <button
            type="button"
            disabled={!ready || run.isPending}
            onClick={() => run.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-accent to-accent-dim px-3 py-1.5 text-[11px] font-semibold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
          >
            {run.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            {run.isPending ? 'Running…' : 'Run'}
          </button>
        </div>
        <textarea
          value={cypher}
          onChange={(e) => setCypher(e.target.value)}
          aria-label="Cypher query"
          className="scrollbar-thin block min-h-[180px] w-full resize-y bg-deep p-4 font-mono text-sm text-text-primary outline-none"
        />
      </div>

      {run.error && (
        <p className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
          {run.error.message}
        </p>
      )}
      {run.data && <ResultTable result={run.data} />}
    </section>
  );
}

function ResultTable({ result }: { readonly result: CypherResult }): JSX.Element {
  if (result.rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border-subtle bg-surface/40 p-3 text-sm text-text-muted">
        No rows.
      </p>
    );
  }
  return (
    <div className="scrollbar-thin overflow-auto rounded-2xl border border-border-subtle bg-surface/60 backdrop-blur">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border-subtle bg-deep/60 text-left font-mono text-[10px] uppercase tracking-wider text-text-muted">
            {result.columns.map((c) => (
              <th key={c} className="px-4 py-2">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle font-mono text-text-secondary">
          {result.rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-hover/40">
              {result.columns.map((c) => (
                <td key={c} className="px-4 py-2 text-text-primary">
                  {formatCell(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}
